/**
 * Tiny vanilla store. Originally Zustand, but Zustand v5 default entry
 * pulls in React. We don't use React, so we hand-roll a minimal pubsub
 * with the same get/set/subscribe API.
 */

import type { ChatChannel } from '@shared/protocol';
import type { ZoneDef } from '@shared/zones';

export interface ChatMessage {
  id: number;
  fromName: string;
  channel: ChatChannel;
  text: string;
  ts: number;
}

export interface NowPlaying {
  stageId: string;
  artist: string;
  trackTitle: string;
}

export interface Identity {
  displayName: string;
  hatColor: number;
}

export interface UIState {
  identity: Identity | null;
  spawnPickerOpen: boolean;
  loadingProgress: number;
  loadingText: string;
  connected: boolean;
  currentZone: string | null;
  knownZones: ZoneDef[];
  playerCount: number;
  nowPlaying: NowPlaying[];
  chat: ChatMessage[];
  festivalTimeLabel: string;
  showOnboarding: boolean;
}

export interface UIActions {
  setIdentity: (id: Identity) => void;
  openSpawnPicker: () => void;
  closeSpawnPicker: () => void;
  setLoading: (progress: number, text?: string) => void;
  setConnected: (v: boolean) => void;
  setCurrentZone: (z: string | null) => void;
  setKnownZones: (zones: ZoneDef[]) => void;
  setPlayerCount: (n: number) => void;
  setNowPlaying: (np: NowPlaying[]) => void;
  pushChat: (msg: ChatMessage) => void;
  clearChat: () => void;
  setFestivalTimeLabel: (s: string) => void;
  dismissOnboarding: () => void;
}

type Store = UIState & UIActions;

let chatId = 1;
let state: UIState = {
  identity: null,
  spawnPickerOpen: true,
  loadingProgress: 0,
  loadingText: 'Loading…',
  connected: false,
  currentZone: null,
  knownZones: [],
  playerCount: 0,
  nowPlaying: [],
  chat: [],
  festivalTimeLabel: '',
  showOnboarding: true
};

const listeners = new Set<() => void>();
function notify(): void {
  for (const l of listeners) l();
}

const actions: UIActions = {
  setIdentity: (identity) => { state = { ...state, identity }; notify(); },
  openSpawnPicker: () => { state = { ...state, spawnPickerOpen: true }; notify(); },
  closeSpawnPicker: () => { state = { ...state, spawnPickerOpen: false }; notify(); },
  setLoading: (loadingProgress, loadingText) => {
    state = { ...state, loadingProgress, loadingText: loadingText ?? state.loadingText };
    notify();
  },
  setConnected: (connected) => { state = { ...state, connected }; notify(); },
  setCurrentZone: (currentZone) => { state = { ...state, currentZone }; notify(); },
  setKnownZones: (knownZones) => { state = { ...state, knownZones }; notify(); },
  setPlayerCount: (playerCount) => { state = { ...state, playerCount }; notify(); },
  setNowPlaying: (nowPlaying) => { state = { ...state, nowPlaying }; notify(); },
  pushChat: (msg) => {
    state = {
      ...state,
      chat: [...state.chat.slice(-49), { ...msg, id: chatId++ }]
    };
    notify();
  },
  clearChat: () => { state = { ...state, chat: [] }; notify(); },
  setFestivalTimeLabel: (festivalTimeLabel) => { state = { ...state, festivalTimeLabel }; notify(); },
  dismissOnboarding: () => { state = { ...state, showOnboarding: false }; notify(); }
};

export const useUI = {
  getState: (): Store => ({ ...state, ...actions }),
  subscribe: (fn: () => void): (() => void) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }
};
